import React from 'react';
import { FileCheck, ShieldCheck, ExternalLink } from 'lucide-react';

const GreenSheet = ({ history, title = "Green Sheet / Note Sheet", onPageClick }) => {
  if (!history || history.length === 0) return <div className="text-center py-4 text-gray-500">No history available</div>;

  const parseRemarks = (text) => {
    if (!text) return null;
    
    // Regex for [Page X] or (Page X) or just Page X (case insensitive)
    // Matches: [Page 4], (Page 4), Page 4, page 4
    const pageRegex = /\[Page\s*(\d+)\]|\(Page\s*(\d+)\)|Page\s*(\d+)/gi;
    
    const elements = [];
    let lastIndex = 0;
    
    text.replace(pageRegex, (match, p1, p2, p3, offset) => {
        // Push text before match
        if (offset > lastIndex) {
            elements.push(text.substring(lastIndex, offset));
        }
        
        const pageNum = p1 || p2 || p3;
        elements.push(
            <span 
                key={offset} 
                onClick={() => onPageClick && onPageClick(parseInt(pageNum))}
                className="text-indigo-600 cursor-pointer hover:underline font-medium inline-flex items-center gap-0.5 bg-indigo-50 px-1 rounded mx-0.5 align-middle"
                title={`Open PDF at Page ${pageNum}`}
            >
                {match} <ExternalLink className="w-3 h-3" />
            </span>
        );
        
        lastIndex = offset + match.length;
        return match;
    });
    
    if (lastIndex < text.length) {
        elements.push(text.substring(lastIndex));
    }
    
    return elements.length > 0 ? elements : text;
  };

  return (
    <div className="bg-[#f0fdf4] border border-green-200 rounded-sm shadow-inner min-h-[800px] p-8 relative overflow-hidden print:shadow-none print:border-0 print:m-0 print:p-0">
      {/* Watermark-like background effect - Hidden in print to save ink/clarity or kept if desired. Kept for now as it's CSS */}
      <div className="absolute inset-0 pointer-events-none opacity-5 flex items-center justify-center print:opacity-0">
        <FileCheck className="w-64 h-64 text-green-800" />
      </div>

      {/* Red Margin Line */}
      <div className="absolute top-0 bottom-0 left-12 w-0.5 bg-red-300/50 print:left-8 print:bg-red-300"></div>

      {/* Header for Print */}
      <div className="hidden print:block text-center mb-8 border-b border-green-800 pb-4">
        <h1 className="text-2xl font-sans font-bold text-green-900">{title}</h1>
      </div>

      <div className="space-y-8 relative z-10 pl-8 print:pl-4">
        {history.map((item, index) => (
          <div key={item.id} className="border-b border-green-200/50 pb-6 last:border-0 print:break-inside-avoid">
            <div className="flex justify-between items-start mb-2">
              <span className="text-xs font-mono text-green-800/60 print:text-gray-600">Note #{history.length - index}</span>
              <span className="text-xs font-mono text-green-800/60 print:text-gray-600">
                {new Date(item.createdAt).toLocaleDateString('en-IN', { 
                  day: '2-digit', month: 'short', year: 'numeric', 
                  hour: '2-digit', minute: '2-digit' 
                })}
              </span>
            </div>
            
            <div className="font-sans text-gray-800 leading-relaxed text-base mb-6 print:text-black">
              {item.remarks ? (
                <p className="whitespace-pre-wrap">{parseRemarks(item.remarks)}</p>
              ) : (
                <p className="italic text-gray-500/80">
                  [System Generated] Action: {item.action} - Status updated to {item.status || 'N/A'}. 
                  Forwarded to {item.toUserName || 'N/A'}.
                </p>
              )}
            </div>

            {/* Signature Block */}
            <div className="flex flex-col items-end mt-4 mr-4 relative">
              {/* Digital Stamp/Seal Watermark */}
              <div className="absolute -top-6 -right-4 opacity-10 pointer-events-none transform rotate-[-12deg] select-none print:opacity-20">
                 <div className="border-4 border-green-800 rounded-full w-32 h-32 flex items-center justify-center">
                    <div className="border-2 border-green-800 rounded-full w-28 h-28 flex items-center justify-center flex-col text-center p-2">
                       <span className="text-green-900 font-bold text-[10px] uppercase">DAK SYSTEM</span>
                       <span className="text-green-900 font-bold text-[8px] uppercase mt-1">Digitally Signed</span>
                       <span className="text-green-900 font-bold text-[8px] uppercase mt-1">{new Date(item.createdAt).toLocaleDateString()}</span>
                    </div>
                 </div>
              </div>

              <div className="text-right relative z-10">
                <div className="flex items-center justify-end gap-1 text-green-700 mb-1 print:hidden">
                   <ShieldCheck className="w-4 h-4" />
                   <span className="text-[10px] font-semibold uppercase tracking-wider">Digitally Signed</span>
                </div>
                <p className="font-bold text-gray-900 uppercase tracking-wide text-sm print:text-black">
                  {item.fromUserName || 'System'}
                </p>
                <p className="text-xs text-gray-600 italic print:text-gray-800">
                  {item.fromUserRole || 'Officer'}
                </p>
                {item.fromDepartment && (
                  <p className="text-xs text-gray-500 print:text-gray-800">
                    {item.fromDepartment}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GreenSheet;
